<!-- avatar.component.html -->
<div class="avatar-container position-relative d-inline-block"
     [class.organization-avatar-container]="isOrganization()">
    <!-- Visually Hidden Checkbox for Accessibility (only when not in upload mode) -->
    @if (isSelectable() && !showUploadControls() && !isOrganization()) {
        <input
            type="checkbox"
            class="visually-hidden-checkbox position-absolute"
            [id]="'avatar-checkbox-' + (identity()?.userId || identity()?.memberId)"
            [checked]="isSelected()"
            [attr.aria-label]="'Select ' + identity()?.firstName + ' ' + identity()?.lastName"
            (change)="onCheckboxChange($event)"
            (click)="$event.stopPropagation()"
        />
    }

    @if (isOrganization()) {
        @if (hasOrganizationAvatarImage()) {
            <!-- Organization Avatar Display: plain image with configurable max-width -->
            <img id="organizationAvatarImage"
                 [src]="identity()?.avatarFileId"
                 [alt]="altText"
                 class="organization-avatar-image"
                 [style.max-width.px]="imageMaxWidthPx()"
                 [class.placeholder]="isLoading()" />
        } @else {
            <!-- Organization default state: fa-duotone-icon -->
            <div class="organization-avatar-icon bg-primary text-white d-flex align-items-center justify-content-center rounded-circle"
                 [class.placeholder]="isLoading()">
                @if (isLoading()) {
                    <div class="spinner-border text-white" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                } @else {
                    <fa-duotone-icon [icon]="fallbackIcon()" secondaryOpacity="0.5"></fa-duotone-icon>
                }
            </div>
        }
    } @else {
        <!-- Existing Avatar Display -->
        <div [class]="avatarClass"
            (click)="avatarClicked()"
            [class.cursor-pointer]="isSelectable() && !showUploadControls()"
            [attr.aria-hidden]="!showUploadControls() ? 'true' : null"
            [attr.role]="!showUploadControls() ? 'presentation' : null">
            @if(isSelected() == true) {
                <div id="avatarSelected" [class]="avatarClass" [class.cursor-pointer]="isSelectable() && !showUploadControls()">
                    <div class="mx-auto fs-4 fw-semibold symbol-label" [style.backgroundColor]="colorClass"
                        [class]="colorClass">
                        <fa-duotone-icon icon="check" secondaryOpacity="1" />
                    </div>
                </div>
            }
            @else {
                @if (showInitials) {
                    <div id="avatarInitials" [class]="avatarClass + ' symbol-label'" [class.cursor-pointer]="isSelectable() && !showUploadControls()"
                        [style.backgroundColor]="colorClass" [style.fontSize]="`${initialsFontSize()}px`"
                        [class.placeholder]="isLoading()">
                        {{initials}}
                    </div>
                } @else {
                    <div id="avatarImage" [class]="avatarClass" [class.cursor-pointer]="isSelectable() && !showUploadControls()">
                        <img [src]="identity()?.avatarFileId" [alt]="altText" class="mx-auto symbol-label"
                            [class.placeholder]="isLoading()" />
                    </div>
                }
            }
        </div>
    }

    <!-- Upload Controls (only shown when showUploadControls is true) -->
    @if (showUploadBadge()) {
        <!-- Upload Badge -->
        <button type="button" class="btn btn-primary btn-sm avatar-badge upload-badge"
            (click)="triggerFileInput()"
            [disabled]="isUploading()"
            title="Upload new avatar"
            *ifHasPermission="uploadPermissionOptions()">
            @if (isUploading()) {
                <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Uploading...</span>
                </div>
            } @else {
                <fa-duotone-icon icon="camera" class="fa-xs"></fa-duotone-icon>
            }
        </button>

        @if (identity()?.avatarFileId) {
            <button type="button" class="btn btn-danger btn-sm avatar-badge remove-badge"
                [class.d-none]="showCropper()"
                (click)="removeAvatar()" title="Remove avatar"
                *ifHasPermission="uploadPermissionOptions()">
                    <fa-duotone-icon icon="xmark" class="fa-xs" [secondaryOpacity]="0.9"></fa-duotone-icon>
            </button>
        }
    }
</div>

<!-- Error Messages -->
@if (hasError() && showUploadControls()) {
    <div class="alert alert-danger overflow-hidden position-absolute mt-2 avatar-error-alert" 
         role="alert">
        <small>{{ errorMessage() }}</small>
        <button type="button" class="btn-close" (click)="clearError()" aria-label="Close"></button>
    </div>
}

<!-- Hidden File Input -->
@if (showUploadControls()) {
    <input #fileInput type="file" class="d-none" accept="image/*" (change)="onFileSelected($event)">
}

<!-- Image Cropper Modal -->
@if (showCropper() && showUploadControls()) {
<div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">Crop Your Avatar</h5>
            </div>
            <div class="modal-body">
                <div class="cropper-container">
                    @if (imageChangedEvent()) {
                        <image-cropper
                            [imageChangedEvent]="imageChangedEvent()"
                            [maintainAspectRatio]="!isOrganization()"
                            [aspectRatio]="1"
                            format="png"
                            [roundCropper]="!isOrganization()"
                            [onlyScaleDown]="false"
                            [autoCrop]="true"
                            [containWithinAspectRatio]="true"
                            [hideResizeSquares]="false"
                            (imageCropped)="imageCropped($event)"
                            (loadImageFailed)="loadImageFailed()">
                        </image-cropper>
                    } @else {
                        <div class="text-center p-4">
                            <p>Loading image...</p>
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" 
                        (click)="cancelCrop()"
                        [disabled]="isUploading()">
                    Cancel
                </button>
                <button type="button" class="btn btn-primary" 
                        [disabled]="!canApplyCrop()" 
                        (click)="applyCrop()">
                    @if (isUploading()) {
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Uploading...
                    } @else {
                        Apply
                    }
                </button>
            </div>
        </div>
    </div>
</div>

<!-- Modal Backdrop -->
<div class="modal-backdrop fade show" (click)="cancelCrop()">
</div>
}